import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Send, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { New: "bg-sky-100 text-sky-700", Open: "bg-indigo-100 text-indigo-700", "In Progress": "bg-amber-100 text-amber-800", "Waiting on User": "bg-orange-100 text-orange-800", Resolved: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-600" };

export default function MyTickets({ startNew = false, prefill = null } = {}) {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], priorities: [], statuses: [] });
  const [rows, setRows] = useState(null);
  const [create, setCreate] = useState(params.get("new") === "1" || startNew);
  const [active, setActive] = useState(null);

  const load = useCallback(() => api.get("/support-tickets/my").then((r) => setRows(r.data.items)).catch(() => setRows([])), []);
  useEffect(() => { load(); api.get("/support-tickets/meta").then((r) => setMeta(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { const id = params.get("ticket"); if (id) openTicket(id); }, [params]);

  const openTicket = (id) => api.get(`/support-tickets/${id}`).then((r) => setActive(r.data)).catch((e) => toast.error(apiError(e)));

  if (active) return <TicketDetail t={active} onBack={() => { setActive(null); setParams({}); load(); }} onRefreshed={setActive} />;

  return (
    <div>
      <PageHeader title="My Support Tickets" description="Submit a request and track responses from the support team."
        actions={<Button className="bg-brand hover:bg-brand-strong" onClick={() => setCreate(true)} data-testid="new-ticket-button"><Plus className="w-4 h-4 mr-1.5" />New Ticket</Button>} />
      {!rows ? null : rows.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="No tickets yet" description="Submit a ticket and the support team will follow up here." />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y" data-testid="my-tickets-list">
          {rows.map((t) => (
            <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`my-ticket-${t.id}`} className="w-full text-left px-4 py-3 hover:bg-slate-50">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800 text-sm truncate">{t.has_unread && <span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-1.5 align-middle" data-testid={`ticket-unread-${t.id}`} />}{t.subject}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 shrink-0 ${STATUS_COLOR[t.status_label] || "bg-slate-100 text-slate-600"}`}>{t.status_label}</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{t.code} · {t.category} · Updated {new Date(t.updated_at).toLocaleString()}</p>
            </button>
          ))}
        </div>
      )}
      {create && <CreateDialog meta={meta} initial={{ subject: params.get("subject") || prefill?.subject || "", description: params.get("description") || prefill?.description || "" }} onClose={() => { setCreate(false); setParams({}); }} onCreated={(t) => { setCreate(false); setParams({}); load(); openTicket(t.id); }} />}
    </div>
  );
}

function CreateDialog({ meta, onClose, onCreated, initial }) {
  const [f, setF] = useState({ subject: initial?.subject || "", category: "Other", priority: "normal", description: initial?.description || "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.subject.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/support-tickets", { ...f, current_page: document.referrer || window.location.pathname });
      toast.success("Ticket submitted"); onCreated(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="create-ticket-dialog">
        <DialogHeader><DialogTitle>Submit a Support Ticket</DialogTitle><DialogDescription>Tell us what you need help with and the support team will follow up here.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject" placeholder="Briefly, what do you need help with?" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-60">{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-semibold">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs font-semibold">Description</Label><Textarea rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ticket-description" placeholder="Include steps, what you expected, and what happened." /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="ticket-submit"><Send className="w-4 h-4 mr-1.5" />{saving ? "Submitting…" : "Submit"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TicketDetail({ t, onBack, onRefreshed, staff = false }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/support-tickets/${t.id}/reply`, { body: reply }); setReply(""); onRefreshed(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto" data-testid="ticket-detail">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-brand flex items-center gap-1 mb-4" data-testid="ticket-back"><ChevronLeft className="w-4 h-4" />Back</button>
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-xl font-bold text-slate-900" data-testid="ticket-detail-subject">{t.subject}</h1>
        <span className={`text-xs rounded-full px-2.5 py-1 shrink-0 ${STATUS_COLOR[t.status_label] || "bg-slate-100 text-slate-600"}`} data-testid="ticket-detail-status">{t.status_label}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">{t.code} · {t.category} · {t.priority} · Opened {new Date(t.created_at).toLocaleString()}</p>
      {t.description && <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3">{t.description}</p>}
      <div className="mt-5 space-y-3" data-testid="ticket-thread">
        {(t.messages || []).map((m) => (
          <div key={m.id} className={`rounded-lg p-3 text-sm ${m.type === "note" ? "bg-amber-50 border border-amber-200" : "bg-white border border-slate-200"}`} data-testid={`ticket-msg-${m.id}`}>
            <p className="text-xs font-medium text-slate-500 mb-1">{m.author_name}{m.type === "note" ? " · Internal note" : ""} · {new Date(m.created_at).toLocaleString()}</p>
            <p className="text-slate-700 whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}
      </div>
      {t.status_label !== "Closed" && (
        <div className="mt-4 flex gap-2">
          <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a reply…" data-testid="ticket-reply-input" onKeyDown={(e) => e.key === "Enter" && send()} />
          <Button onClick={send} disabled={busy} className="bg-brand hover:bg-brand-strong" data-testid="ticket-reply-send"><Send className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}
